// 共用系統訊息／錯誤提示窗 — 對照 Unity SlotFramework 的唯一一套通用對話框忠實移植：
//   Script : SlotFramework/Script/UI/Dialog/{Dialog.cs, DialogButton.cs}
//   Prefab : SlotFramework/Prefab/UI/Dialog/{Dialog.prefab, DialogButton.prefab}
//   字表   : Dialog_StringTable（13 語系，已匯出為本專案 i18n 的 dialog.* key）
//
// Unity 只有這一個 prefab，所有型態（單鈕／多鈕／可關閉／不可關閉）都由參數控制，
// 錯誤訊息、被踢下線、餘額不足、MaxWin、Replay 全部走同一個窗。
//
// 版面（Dialog.prefab 序列化原值，Panel 中心為原點、Unity y 向上 → PixiJS 取反）：
//   Canvas    CanvasScaler reference 1080×1080、ScaleWithScreenSize + Expand（match=0）
//             → 縮放係數 = min(螢幕寬, 螢幕高) / 1080（**以較短邊為準**，與專案其他
//               1920×1080／1080×1920 的 unityScale* helper 不同，故本檔自行換算）
//   ImgBackground 全螢幕遮罩，無圖、純黑 alpha 0.3137255，攔截點擊
//   Panel         920×580，畫面正中央
//   Background_1  pic_popupBg（Sliced）鋪滿 Panel，染黑 alpha 0.63529414
//   Background    pic_popupBg（Sliced）鋪滿 Panel，白 alpha 1，材質 FrostedGlass
//   Title         pic_titleBg（Sliced）錨頂、sizeDelta (0,70)、pivot 頂中 → 中心 y=+255
//   Title/Text    fontSize 36 Bold、#F8C232、置中
//   Message       錨滿版、sizeDelta (-100,-250)、aPos (0,25) → 820×330 @ y=+25
//                 TMP AutoSize 開啟（18~48）、#FBF2B3、置中、自動換行
//   ButtonsHLayout 錨底、sizeDelta (0,80)、aPos (0,40) → 中心 y=−250；
//                 HorizontalLayoutGroup padding.bottom=50、spacing=16、MiddleCenter、
//                 不控制子尺寸 → 可用區高 30、中心相對容器 +25 → 按鈕中心 y=−225
//   DialogButton  256×92；style "OK"=btn_start／"Cancel"=btn_cancel（樣式字串為空時
//                 Unity fallback 到 _styleObjects[0]＝"OK"）；文字 fontSize 48 AutoSize
//                 18~48、#EFCF50、寬 230、相對鈕心 y=+4
//   Close         btn_close 84×84，錨 Panel 右上、aPos (−35,−35) → 中心 (425, 255)
//
// [已知差異] Background 的 FrostedGlass 材質（_BlurSize 5 的 UI 毛玻璃 shader，取樣背後畫面模糊）
//   無法在 PixiJS 還原——backdrop 模糊需額外引入 pixi-filters 並每幀取樣，成本遠高於收益。
//   本移植保留兩層 pic_popupBg 疊放（黑底層＋原色層），形狀／層次／色調一致，僅少了背景透出的模糊。
// [已知差異] Unity 無開關窗動畫（Dialog.cs 只做 _window.SetActive），本移植同樣不加動畫。

import { Container, Graphics, NineSliceSprite, Sprite, Text, type TextStyleOptions } from 'pixi.js'
import type { Orientation, Resizable } from '../../../framework/render/design'
import { fontFamilyForLocale } from '../../../framework/i18n/i18n'
import { getUiTexture } from '../../assets/UiTextures'
import { reportModalOpen } from './modalGate'

/** CanvasScaler reference（Dialog.prefab 為正方形 1080×1080、Expand）。 */
const CANVAS_REF = 1080
/** Panel sizeDelta。 */
const PANEL_W = 920
const PANEL_H = 580
/** pic_popupBg 原生 300×300，九宮格邊界 45px 保住圓角。 */
const POPUP_SLICE = 45
/** Background_1 的染黑 alpha（Image m_Color.a）。 */
const PANEL_SHADE_ALPHA = 0.63529414
/** ImgBackground 全螢幕遮罩 alpha。 */
const DIM_ALPHA = 0.3137255

/** Title 條高與文字中心 y（Panel 座標，Unity y 向上）。 */
const TITLE_H = 70
const TITLE_CY = PANEL_H / 2 - TITLE_H / 2
const TITLE_FONT = 36

/** Message 區（sizeDelta (-100,-250)、aPos (0,25)）。 */
const MSG_W = PANEL_W - 100
const MSG_H = PANEL_H - 250
const MSG_CY = 25
const MSG_FONT_MAX = 48
const MSG_FONT_MIN = 18

/** DialogButton 尺寸與排列（HorizontalLayoutGroup spacing 16、可用區中心偏移 +25）。 */
const BTN_W = 256
const BTN_H = 92
const BTN_SPACING = 16
const BTN_CY = -PANEL_H / 2 + 40 + 25
const BTN_TEXT_W = 230
const BTN_TEXT_CY = 4
const BTN_FONT_MAX = 48
const BTN_FONT_MIN = 18

/** Close 鈕（Panel 右上 (460,290) + aPos (−35,−35)）。 */
const CLOSE_SIZE = 84
const CLOSE_CX = PANEL_W / 2 - 35
const CLOSE_CY = PANEL_H / 2 - 35

/** Unity Button ColorTint 預設色階（Normal / Highlighted / Pressed）。 */
const TINT_NORMAL = 0xffffff
const TINT_HIGHLIGHTED = 0xf5f5f5
const TINT_PRESSED = 0xc8c8c8

/** DialogButton 的樣式（DialogButton.prefab 的 _styleObjects：索引 0 為預設）。 */
export type DialogButtonStyle = 'OK' | 'Cancel'

/** 對應 Unity `ActionButton`（Dialog.cs）：按鈕文字、點擊行為、樣式。 */
export interface DialogAction {
  readonly text: string
  readonly action?: (() => void) | undefined
  readonly style?: DialogButtonStyle | undefined
}

/** 一次 ShowDialog 的完整內容（對應 DialogMediator.ShowDialog 的參數組）。 */
export interface DialogContent {
  readonly title: string
  readonly message: string
  readonly actions: readonly DialogAction[]
  /** 是否顯示右上關閉鈕（Unity showCloseBtn，預設 true）。 */
  readonly showCloseButton: boolean
  /** 是否可點全螢幕遮罩關窗（Unity needCloseByTapBG，預設 false）。 */
  readonly closeByTapBackground: boolean
}

/** 金色標題字（Title/Text：#F8C232 Bold）。 */
function titleStyle(): TextStyleOptions {
  return { fontFamily: fontFamilyForLocale(), fontSize: TITLE_FONT, fill: 0xf8c232, fontWeight: 'bold', align: 'center' }
}

/** 淡黃內文（Message：#FBF2B3，置中換行）。 */
function messageStyle(): TextStyleOptions {
  return {
    fontFamily: fontFamilyForLocale(),
    fontSize: MSG_FONT_MAX,
    fill: 0xfbf2b3,
    align: 'center',
    wordWrap: true,
    wordWrapWidth: MSG_W,
  }
}

/** 按鈕字（#EFCF50）。 */
function buttonTextStyle(): TextStyleOptions {
  return { fontFamily: fontFamilyForLocale(), fontSize: BTN_FONT_MAX, fill: 0xefcf50, align: 'center' }
}

/**
 * TMP Auto Size 等價縮字：從 max 級數起，逐級縮到內容塞得進 (maxW × maxH) 為止，下限 min。
 * Unity 的 TMP autoSizing 會在字級區間內找最大可容納值，這裡以 1pt 為步進做同樣的事。
 */
function fitFontSize(text: Text, maxW: number, maxH: number, max: number, min: number): void {
  for (let size = max; size >= min; size -= 1) {
    text.style.fontSize = size
    if (text.style.wordWrap) text.style.wordWrapWidth = maxW
    if (text.width <= maxW && text.height <= maxH) return
  }
  text.style.fontSize = min
}

/** 一顆對話框按鈕（DialogButton.prefab：底圖 256×92 ＋ 置中文字）。 */
class DialogButtonView extends Container {
  private readonly background: Sprite
  // ⚠️ 不可命名為 label：PixiJS v8 的 Container already has `label: string`（v7 name 的替代）。
  private readonly caption: Text

  constructor(action: DialogAction, onClick: () => void) {
    super()
    // 樣式字串為空時 Unity fallback 到 _styleObjects[0]（"OK"）。
    const style: DialogButtonStyle = action.style ?? 'OK'
    this.background = new Sprite(getUiTexture(style === 'Cancel' ? 'btnCancel' : 'btnStart'))
    this.background.anchor.set(0.5)
    this.background.setSize(BTN_W, BTN_H)
    this.caption = new Text({ text: action.text, style: buttonTextStyle() })
    this.caption.anchor.set(0.5)
    this.caption.position.set(0, -BTN_TEXT_CY)
    fitFontSize(this.caption, BTN_TEXT_W, BTN_H, BTN_FONT_MAX, BTN_FONT_MIN)
    this.addChild(this.background, this.caption)

    this.eventMode = 'static'
    this.cursor = 'pointer'
    this.on('pointerover', () => (this.background.tint = TINT_HIGHLIGHTED))
    this.on('pointerout', () => (this.background.tint = TINT_NORMAL))
    this.on('pointerdown', () => (this.background.tint = TINT_PRESSED))
    this.on('pointerup', () => (this.background.tint = TINT_NORMAL))
    this.on('pointerupoutside', () => (this.background.tint = TINT_NORMAL))
    this.on('pointertap', onClick)
  }
}

/**
 * 共用系統訊息窗。由 dialogMediator 統一驅動（對應 Unity 的靜態 DialogMediator ↔ Dialog View 分工），
 * 呼叫端不直接持有本類別。
 */
export class SystemDialog extends Container implements Resizable {
  /** 全螢幕遮罩（ImgBackground）。 */
  private readonly dim = new Graphics()
  /** 1080×1080 設計座標層，原點＝Panel 中心＝螢幕中心。 */
  private readonly root = new Container()
  private readonly shade: NineSliceSprite
  private readonly panelBg: NineSliceSprite
  private readonly titleBg: Sprite
  private readonly titleText = new Text({ text: '', style: titleStyle() })
  private readonly messageText = new Text({ text: '', style: messageStyle() })
  private readonly buttonRow = new Container()
  private readonly closeButton = new Container()

  private closeByTapBackground = false
  private onCloseRequested: (() => void) | null = null

  constructor() {
    super()
    this.visible = false
    this.eventMode = 'static'

    this.dim.eventMode = 'static'
    this.dim.on('pointertap', () => {
      if (this.closeByTapBackground) this.onCloseRequested?.()
    })

    this.shade = this.createPanelSprite()
    this.shade.tint = 0x000000
    this.shade.alpha = PANEL_SHADE_ALPHA
    // [已知差異] Unity 這層帶 FrostedGlass 毛玻璃材質（見檔頭），本移植以原色貼圖呈現。
    this.panelBg = this.createPanelSprite()

    this.titleBg = new Sprite(getUiTexture('titleBg'))
    this.titleBg.anchor.set(0.5)
    this.titleBg.setSize(PANEL_W, TITLE_H)
    this.titleBg.position.set(0, -TITLE_CY)
    this.titleText.anchor.set(0.5)
    this.titleText.position.set(0, -TITLE_CY)

    this.messageText.anchor.set(0.5)
    this.messageText.position.set(0, -MSG_CY)

    this.buttonRow.position.set(0, -BTN_CY)
    this.buildCloseButton()

    this.root.addChild(
      this.shade,
      this.panelBg,
      this.titleBg,
      this.titleText,
      this.messageText,
      this.buttonRow,
      this.closeButton,
    )
    this.addChild(this.dim, this.root)
  }

  /** Panel 底圖（pic_popupBg 九宮格鋪滿 920×580，以中心定位）。 */
  private createPanelSprite(): NineSliceSprite {
    const sprite = new NineSliceSprite({
      texture: getUiTexture('popupBg'),
      leftWidth: POPUP_SLICE,
      topHeight: POPUP_SLICE,
      rightWidth: POPUP_SLICE,
      bottomHeight: POPUP_SLICE,
    })
    sprite.width = PANEL_W
    sprite.height = PANEL_H
    sprite.position.set(-PANEL_W / 2, -PANEL_H / 2)
    return sprite
  }

  /** 右上關閉鈕（btn_close 84×84）。 */
  private buildCloseButton(): void {
    const cross = new Sprite(getUiTexture('btnClose'))
    cross.anchor.set(0.5)
    cross.setSize(CLOSE_SIZE, CLOSE_SIZE)
    this.closeButton.addChild(cross)
    this.closeButton.position.set(CLOSE_CX, -CLOSE_CY)
    this.closeButton.eventMode = 'static'
    this.closeButton.cursor = 'pointer'
    this.closeButton.on('pointerover', () => (cross.tint = TINT_HIGHLIGHTED))
    this.closeButton.on('pointerout', () => (cross.tint = TINT_NORMAL))
    this.closeButton.on('pointerdown', () => (cross.tint = TINT_PRESSED))
    this.closeButton.on('pointerup', () => (cross.tint = TINT_NORMAL))
    this.closeButton.on('pointerupoutside', () => (cross.tint = TINT_NORMAL))
    this.closeButton.on('pointertap', () => this.onCloseRequested?.())
  }

  get isOpen(): boolean {
    return this.visible
  }

  /**
   * 顯示對話框（對應 Dialog.ShowDialog）。
   * @param content 標題／內文／按鈕組與關閉行為（文字皆為已在地化的成品字串）
   * @param onButton 按下任一按鈕時的回呼，帶按鈕索引；Unity 由 DialogMediator.OnClickButton 統一處理
   * @param onClose 按右上關閉鈕或點遮罩關窗時的回呼
   */
  showDialog(content: DialogContent, onButton: (index: number) => void, onClose: () => void): void {
    this.titleText.text = content.title
    this.messageText.text = content.message
    fitFontSize(this.messageText, MSG_W, MSG_H, MSG_FONT_MAX, MSG_FONT_MIN)

    this.buttonRow.removeChildren().forEach((child) => child.destroy({ children: true }))
    content.actions.forEach((action, index) => {
      this.buttonRow.addChild(new DialogButtonView(action, () => onButton(index)))
    })
    this.layoutButtons(content.actions.length)

    this.closeButton.visible = content.showCloseButton
    this.closeByTapBackground = content.closeByTapBackground
    this.onCloseRequested = onClose

    this.visible = true
    reportModalOpen(this, true)
  }

  /** 關閉（對應 Dialog.HideDialog：只切 active，無動畫）。 */
  hideDialog(): void {
    this.visible = false
    reportModalOpen(this, false)
  }

  /** 依 HorizontalLayoutGroup（spacing 16、置中、不控制子尺寸）水平排列按鈕。 */
  private layoutButtons(count: number): void {
    if (count === 0) return
    const total = count * BTN_W + (count - 1) * BTN_SPACING
    this.buttonRow.children.forEach((child, index) => {
      child.position.set(-total / 2 + BTN_W / 2 + index * (BTN_W + BTN_SPACING), 0)
    })
  }

  resize(width: number, height: number, _orientation: Orientation): void {
    this.dim.clear().rect(0, 0, width, height).fill({ color: 0x000000, alpha: DIM_ALPHA })
    // Expand + 正方形 reference → 以較短邊決定縮放（橫版看寬高中較小者，直版亦然）。
    this.root.scale.set(Math.min(width, height) / CANVAS_REF)
    this.root.position.set(width / 2, height / 2)
  }
}
